"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { gsap } from "gsap"
import { API_BASE_URL } from "../utils/Constant"

const LoginForm = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const formRef = useRef(null)

  useEffect(() => {
    // Form animation
    const formElement = formRef.current
    const formChildren = formElement?.querySelectorAll(".form-item") || []
    gsap.set(formChildren, { y: 50, opacity: 0 })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(formChildren, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.2,
              ease: "power3.out",
            })
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.3 }
    )

    if (formElement) observer.observe(formElement)

    return () => {
      if (formElement) observer.unobserve(formElement)
    }
  }, [])

  const handleLogin = async (e) => {
    e.preventDefault()
    if (isLoading) return

    setIsLoading(true)
    setError("")

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      })

      const data = await response.json()

      if (response.ok) {
        localStorage.setItem("access_token", data.access_token)
        navigate("/dashboard")
      } else {
        setError(data.message || "Invalid credentials. Please try again.")
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again later.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-720 text-white overflow-hidden">
      <section className="relative z-10 min-h-screen flex items-center justify-center py-16 px-4 md:px-6 lg:px-8">
        <div ref={formRef} className="w-full max-w-md bg-slate-700/50 backdrop-blur-sm p-8 rounded-xl shadow-lg">
          <h2 className="form-item text-2xl md:text-3xl font-bold text-center mb-12">Welcome Back!</h2>
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="form-item">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-6 py-4 text-lg bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="form-item">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-6 py-4 text-lg bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {error && <p className="form-item text-red-400 text-base">{error}</p>}
            <button
              type="submit"
              className={`form-item w-full py-4 px-8 text-lg font-semibold rounded-lg transition-colors ${
                isLoading ? "bg-slate-600 text-slate-300 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              disabled={isLoading}
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </button>
          </form>
          <div className="form-item mt-4 text-center">
            <button
              className="text-blue-500 hover:underline text-sm"
              onClick={() => navigate("/auth/forgot-password")}
            >
              Forgot Password?
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default LoginForm